import { combineReducers } from "redux";
import { configureStore } from "@reduxjs/toolkit";
import { createLogger } from "redux-logger";
import createSagaMiddleware from "@redux-saga/core";
import authSaga from "./auth/auth.saga";
import authReducer from "./auth/auth.reducer";
import globalSaga from "./global/global.saga";
import globalReducer from "./global/global.reducer";
import { GlobalActionType, setNoConnectionDialogOpen } from "./global/global.actions";
import usersSaga from "./users/users.saga";
import usersReducer from "./users/users.reducer";
import knowledgeBaseItemReducer from "./knowledge-base-item/knowledge-base-item.reducer";
import knowledgeBaseItemSaga from "./knowledge-base-item/knowledge-base-item.saga";
import psWorkspaceSaga from "./ps-workspace/ps-workspace.saga";
import psWorkspaceReducer from "./ps-workspace/ps-workspace.reducer";
import rsWorkspaceSaga from "./rs-workspace/sagas/rs-workspace.saga";
import rsWorkspaceReducer from "./rs-workspace/rs-workspace.reducer";
import psWorkspaceListSaga from "./ps-workspace-list/ps-workspace-list.saga";
import psWorkspaceListReducer from "./ps-workspace-list/ps-workspace-list.reducer";
import rsWorkspaceListSaga from "./rs-workspace-list/rs-workspace-list.saga";
import rsWorkspaceListReducer from "./rs-workspace-list/rs-workspace-list.reducer";
import cellModelSaga from "./cell-model/cell-model.saga";
import cellModelReducer from "./cell-model/cell-model.reducer";
import cellModelListReducer from "./cell-model-list/cell-model-list.reducer";
import cellModelListSaga from "./cell-model-list/cell-model-list.saga";
import recipeListReducer from "./recipe-list/recipe-list.reducer";
import recipeListSaga from "./recipe-list/recipe-list.saga";
import recipeSaga from "./recipe/recipe.saga";
import recipeReducer from "./recipe/recipe.reducer";
import licensingSaga from "./licensing/licensing.saga";
import processDataSaga from "./process-data/process-data.saga";
import licensingReducer from "./licensing/licensing.reducer";
import logger from "../utils/logger";
import { KnowledgeBaseItemActionType } from "./knowledge-base-item/knowledge-base-item.actions";
import { checkServerAvailable } from "utils/network";
import { LicensingActionType } from "./licensing/licensing.actions";
import processDataReducer from "./process-data/process-data.reducer";
import processDataListReducer from "./process-data-list/process-data-list.reducer";
import processDataListSaga from "./process-data-list/process-data-list.saga";
import { KnowledgeBaseItemsListActionType } from "./knowledge-base-items-list/knowledge-base-items-list.actions";
import routerReducer from "store/router/router.reducer";
// new feature reducer has to be registered here
export const rootReducer = combineReducers({
    router: routerReducer,
    auth: authReducer,
    users: usersReducer,
    global: globalReducer,
    knowledgeBaseItem: knowledgeBaseItemReducer,
    psWorkspace: psWorkspaceReducer,
    rsWorkspace: rsWorkspaceReducer,
    psWorkspaceList: psWorkspaceListReducer,
    rsWorkspaceList: rsWorkspaceListReducer,
    cellModelList: cellModelListReducer,
    cellModel: cellModelReducer,
    recipeList: recipeListReducer,
    recipe: recipeReducer,
    licensing: licensingReducer,
    processData: processDataReducer,
    processDataList: processDataListReducer,
});
// new feature saga has to be registered here
const sagas = [
    authSaga,
    globalSaga,
    usersSaga,
    knowledgeBaseItemSaga,
    psWorkspaceSaga,
    rsWorkspaceSaga,
    psWorkspaceListSaga,
    rsWorkspaceListSaga,
    cellModelListSaga,
    cellModelSaga,
    recipeListSaga,
    recipeSaga,
    licensingSaga,
    processDataSaga,
    processDataListSaga,
];
const dev = process.env.NODE_ENV === "development";
const reduxLogger = createLogger({
    collapsed: true,
    predicate: (getState, action) => action.type !== GlobalActionType.SET_SCROLL_ELEMENT,
});
const OFFLINE_ACTIONS_WHITELIST = [
    KnowledgeBaseItemActionType.RECONNECTING,
    GlobalActionType.SET_OFFLINE,
    GlobalActionType.SET_NO_CONNECTION_DIALOG_OPEN,
    GlobalActionType.SET_LAST_REQUESTED_ROUTE,
];
// could be extended, for now included most frequently fired
const HEALTH_CHECK_OMITTED_ACTIONS = [
    KnowledgeBaseItemActionType.SET_ERROR,
    KnowledgeBaseItemActionType.SETUP_ITEM,
    GlobalActionType.SET_NO_CONNECTION_DIALOG_OPEN,
    GlobalActionType.SET_LAST_REQUESTED_ROUTE,
    GlobalActionType.SET_OFFLINE,
    GlobalActionType.SET_RENAMING_PROGRESS,
    LicensingActionType.CHANGE_SEATS_TOAST_VISIBLE_STATUS,
    GlobalActionType.SET_APP_VERSIONS,
    GlobalActionType.SET_SCROLL_ELEMENT,
    LicensingActionType.SET_SEATS,
    KnowledgeBaseItemsListActionType.SET_SEARCH_QUERY,
];
const connectionStatusMiddleWare = ({ dispatch, getState }) => (next) => async (action) => {
    const state = getState();
    const isRecipeScaling = state.psWorkspace.id === null;
    const isEditable = state.knowledgeBaseItem.editable;
    const shouldNotCheckConnection = !isRecipeScaling || isEditable;
    const isOmittedAction = shouldNotCheckConnection ||
        action.type.includes("FAILURE") ||
        action.type.includes("SUCCESSFUL") ||
        action.type.includes("PREVIEW") ||
        HEALTH_CHECK_OMITTED_ACTIONS.includes(action.type);
    const shouldAllowAction = isOmittedAction ? true : await checkServerAvailable();
    if (shouldNotCheckConnection || shouldAllowAction) {
        next(action);
    }
    if (!shouldAllowAction && isRecipeScaling && !isEditable) {
        const isSetNoConnectionAction = action.type === GlobalActionType.SET_NO_CONNECTION_DIALOG_OPEN;
        if (!state.knowledgeBaseItem.editable && !isSetNoConnectionAction) {
            dispatch(setNoConnectionDialogOpen(true));
        }
        if (OFFLINE_ACTIONS_WHITELIST.includes(action.type)) {
            next(action);
        }
    }
};
const sagaMiddleware = createSagaMiddleware({
    onError: (err) => {
        // general fallback for saga errors which are not handled by try ... catch inside saga
        logger.error(err);
    },
});
const createStore = () => {
    const configuringStore = configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat([
            ...(dev ? [reduxLogger] : []),
            connectionStatusMiddleWare,
            sagaMiddleware,
        ]),
        devTools: dev,
    });
    sagas.forEach((saga) => {
        sagaMiddleware.run(saga);
    });
    return configuringStore;
};
const store = createStore();
export { sagas, store };
